import { useState } from 'react';
import { useToast } from '../context/ToastContext';
import { exportDateFormat } from './reuse';
import { continentMap } from './useExportCSV';

// Custom hook for CSV export logic
export const useCSVExport = (processedOptions, sortOrder, headers, dispatch, exportAlerts, handleExportCSV) => {
    const [exportLoading, setLoading] = useState(false);
    const { showToast } = useToast();
    const extractITFValue = (text = "") => {
        if (!text.toUpperCase().includes("ITF")) return null;

        const match = text.match(/\d+/);
        return match ? `${Number(match[0])}k` : null;
    };
    const getContinent = (country = "") => {
        if (!country || country === "No Country") return "N/A";

        for (const [continent, countries] of Object.entries(continentMap)) {
            if (countries.has(country)) {
                return continent.replace(/([A-Z])/g, " $1").trim();
            }
        }
        return "";
    };
    const  flattenAlertToEventRecords = (alert) => {
        const regulators = alert.receivers?.filter(r => r.role === "regulator") || [];
        const regInformed = regulators.length
        ? `Y (${regulators.map(r => r.name).join(" / ")})`
        : "N";
        const  reportedTo = alert.receivers?.filter(r => r.role === "governingbody").map(r => r.name).join(" / ") || "";


        return (alert.events || []).map((event) => ({
            // Alert-level fields
            legacyID: alert.legacyID,
            alertID: alert.alertID,
            createdByGroup: alert.createdByGroup,
            createdByUser: alert.createdByUser,
            timeOfBet: alert.timeOfBet?.join(' / ') ?? "",
            receivedResponseCount: alert.receivedResponseCount,
            updatedAt: exportDateFormat(alert.updatedAt),
            createdAt: exportDateFormat(alert.createdAt),
            ibiaAdvice: alert.ibiaAdvice ?? "",
            description: alert.description?.trim() ?? "",
            opposedPlayer: alert.tags?.filter(t => t.category === 'athleteteam')?.map(t => t.name)?.join(' / ') ?? "",
            marketTags: alert.tags?.filter(t => t.category === 'market')?.map(t => t.name)?.join(' / ') ?? "",
            suspiciousCount: alert.suspiciousCount,
            valueITF: extractITFValue(event.tournamentLevelName) ?? "",
            continent: getContinent(event.locationName),
            createdByGroupName: alert.createdByGroupName,
            assignee: alert.assignee ?? "",
            // Event-level fields
            eventID: event.eventID,
            eventName: event.eventName?.trim() ?? "",
            sportID: event.sportID,
            sportName: event.sportName,
            tournamentID: event.tournamentID,
            tournamentName: event.tournamentName,
            eventDateTime: exportDateFormat(event.dateTime),
            locationID: event.locationID,
            locationName: event.locationName,
            gender: event.gender,
            turnover: event.turnover,
            tournamentLevelName: event.tournamentLevelName ?? "",
            regInformed: regInformed,
            reportedTo: reportedTo,
            customerLocationsCountry: event.customerLocations?.map(loc => loc.country).filter(Boolean).join(" / ") ?? "",
            customerLocationsState: event.customerLocations?.map(loc => loc.state).filter(Boolean).join(" / ") ?? ""
        }));
    }

    const exportCSV = (e) => {
        setLoading(true);
        e.preventDefault();

        const obj = {
            ...processedOptions,
            sort: sortOrder.current === '' ? 'desc' : 'asc'
        };

        exportAlerts(obj, headers, dispatch, response => {
            setLoading(false);
            if (response.result === 'SUCCESS') {
                const data = response.data;
                const flattenedData = data.flatMap(alert => flattenAlertToEventRecords(alert));
                if (flattenedData?.length > 0) {
                    handleExportCSV(flattenedData);
                    showToast("success", "Exported successfully");
                } else {
                    showToast("warn", "No records found");
                }
            } else {
                const error = response?.error;
                showToast("error", error?.errorMsg || error?.summary);
            }
        });
    };

    return {
        exportLoading,
        exportCSV
    };
};
